import UserFriendlyExpressionBuilder from 'app/services/formula/parser/userFriendlyExpressionBuilder/userFriendlyExpressionBuilder';
import _ from 'lodash';

import { jsonStringify } from 'utils/json';

import {
  PREVIEW_DEFAULT_COLLAPSED_LEAF_THRESHOLD,
  PREVIEW_GROUP_OPERATOR,
  PREVIEW_NODE_KIND,
  PREVIEW_NODE_STATUS,
} from './ExpressionPreview.constants';

const GROUP_OPERATORS = new Set([PREVIEW_GROUP_OPERATOR.AND, PREVIEW_GROUP_OPERATOR.OR]);

const isFunctionNode = node =>
  _.isPlainObject(node) && _.isString(node.type) && node.type.toLowerCase() === 'function';

const upperCaseValue = node => (_.isString(node?.value) ? node.value.toUpperCase() : null);

const firstLetterToUpperCase = key =>
  _.isString(key) && !_.isEmpty(key) ? key.charAt(0).toUpperCase() + key.slice(1) : key;

const pascalCaseAstKeys = value => {
  if (_.isArray(value)) {
    return value.map(pascalCaseAstKeys);
  }

  if (_.isPlainObject(value)) {
    return _.mapValues(
      _.mapKeys(value, (_v, key) => firstLetterToUpperCase(key)),
      pascalCaseAstKeys,
    );
  }

  return value;
};

const canonicalNodeString = node => {
  if (_.isArray(node)) {
    return `[${node.map(canonicalNodeString).join(',')}]`;
  }

  if (_.isPlainObject(node)) {
    const entries = Object.keys(node)
      .sort()
      .map(key => `${key}:${canonicalNodeString(node[key])}`);

    return `{${entries.join(',')}}`;
  }

  return jsonStringify(node);
};

const buildLeafText = (node, { isNewExpressionsParsingLogicEnabled }) => {
  try {
    const text = UserFriendlyExpressionBuilder.build(node, false, {
      isNewExpressionsParsingLogicEnabled,
    });

    return _.isEmpty(text) ? null : text;
  } catch {
    return null;
  }
};

const collectFieldPaths = (node, acc) => {
  if (_.isArray(node)) {
    node.forEach(child => collectFieldPaths(child, acc));

    return;
  }

  if (!_.isPlainObject(node)) {
    return;
  }

  const isFieldType = _.isString(node.type) && node.type.toLowerCase() === 'field';

  if (isFieldType && _.isString(node.value) && !_.isEmpty(node.value)) {
    acc.add(node.value);
  }

  if (_.isArray(node.operands)) {
    node.operands.forEach(child => collectFieldPaths(child, acc));
  }
};

const collectLeafFieldPaths = node => {
  const paths = new Set();

  collectFieldPaths(node, paths);

  return Array.from(paths);
};

const buildLeafNode = (ast, { isNewExpressionsParsingLogicEnabled }) => ({
  kind: PREVIEW_NODE_KIND.LEAF,
  operator: null,
  text: buildLeafText(ast, { isNewExpressionsParsingLogicEnabled }),
  fieldPaths: collectLeafFieldPaths(ast),
  expressionJson: jsonStringify(pascalCaseAstKeys(ast)),
  fingerprint: canonicalNodeString(ast),
  status: PREVIEW_NODE_STATUS.UNCHANGED,
  children: [],
});

const buildGroupNode = (operator, children) => ({
  kind: PREVIEW_NODE_KIND.GROUP,
  operator,
  text: null,
  fieldPaths: [],
  expressionJson: null,
  fingerprint: `group:${operator}:[${children.map(child => child.fingerprint).join(',')}]`,
  status: PREVIEW_NODE_STATUS.UNCHANGED,
  children,
});

/**
 * Builds a normalized `PreviewNode` tree from a parsed AST.
 * Function nodes with operator `AND`/`OR` become group nodes;
 * everything else (operator nodes, function nodes like `Any`/`Every`,
 * field/value nodes) becomes a leaf rendered through
 * `UserFriendlyExpressionBuilder`.
 */
export const buildPreviewNodeFromAst = (ast, { isNewExpressionsParsingLogicEnabled } = {}) => {
  if (_.isNil(ast)) {
    return null;
  }

  const operatorValue = upperCaseValue(ast);
  const isGroupAst =
    isFunctionNode(ast) && GROUP_OPERATORS.has(operatorValue) && _.isArray(ast.operands);

  if (!isGroupAst) {
    return buildLeafNode(ast, { isNewExpressionsParsingLogicEnabled });
  }

  const children = ast.operands.map(operand =>
    buildPreviewNodeFromAst(operand, { isNewExpressionsParsingLogicEnabled }),
  );

  return buildGroupNode(operatorValue, children);
};

const countLeavesInNode = node => {
  if (_.isNil(node)) {
    return 0;
  }

  if (node.kind === PREVIEW_NODE_KIND.LEAF) {
    return 1;
  }

  return node.children.reduce((acc, child) => acc + countLeavesInNode(child), 0);
};

export const collectLeafCount = countLeavesInNode;

const indexChildrenByFingerprint = children => {
  const indexByFingerprint = new Map();

  children.forEach((child, index) => {
    if (_.isString(child.fingerprint) && !indexByFingerprint.has(child.fingerprint)) {
      indexByFingerprint.set(child.fingerprint, index);
    }
  });

  return indexByFingerprint;
};

const countSharedFingerprints = (previousChildren, proposedChildren) => {
  const previousSet = new Set(previousChildren.map(child => child.fingerprint));

  return proposedChildren.filter(child => previousSet.has(child.fingerprint)).length;
};

const findGroupMatchByOverlap = (proposedGroup, previousPool) => {
  const matchableCandidates = previousPool
    .map((candidate, index) => ({ candidate, index }))
    .filter(
      ({ candidate }) =>
        candidate.kind === PREVIEW_NODE_KIND.GROUP && candidate.operator === proposedGroup.operator,
    );

  if (_.isEmpty(matchableCandidates)) {
    return null;
  }

  const ranked = matchableCandidates.map(({ candidate, index }) => ({
    candidate,
    index,
    overlap: countSharedFingerprints(candidate.children, proposedGroup.children),
  }));

  const best = _.maxBy(ranked, 'overlap');

  return best?.overlap > 0 ? best : null;
};

const markStatusDeep = (node, status) => ({
  ...node,
  status,
  children: node.children.map(child => markStatusDeep(child, status)),
});

const mergeChildrenForDiff = (proposedChildren, previousChildren) => {
  const remainingPrevious = [...previousChildren];
  const previousIndexByFingerprint = indexChildrenByFingerprint(remainingPrevious);
  const matchedFingerprints = new Set();

  const mergedFromProposed = proposedChildren.map(proposedChild => {
    if (previousIndexByFingerprint.has(proposedChild.fingerprint)) {
      matchedFingerprints.add(proposedChild.fingerprint);

      return markStatusDeep(proposedChild, PREVIEW_NODE_STATUS.UNCHANGED);
    }

    if (proposedChild.kind === PREVIEW_NODE_KIND.GROUP) {
      const overlapMatch = findGroupMatchByOverlap(proposedChild, remainingPrevious);

      if (!_.isNil(overlapMatch)) {
        const matchedPrevious = overlapMatch.candidate;
        remainingPrevious.splice(overlapMatch.index, 1);
        matchedFingerprints.add(matchedPrevious.fingerprint);

        const mergedChildren = mergeChildrenForDiff(
          proposedChild.children,
          matchedPrevious.children,
        );

        return {
          ...proposedChild,
          status: PREVIEW_NODE_STATUS.UNCHANGED,
          children: mergedChildren,
        };
      }
    }

    return markStatusDeep(proposedChild, PREVIEW_NODE_STATUS.ADDED);
  });

  const removed = previousChildren
    .filter(
      previousChild =>
        _.isString(previousChild.fingerprint) &&
        !matchedFingerprints.has(previousChild.fingerprint),
    )
    .map(previousChild => markStatusDeep(previousChild, PREVIEW_NODE_STATUS.REMOVED));

  return [...mergedFromProposed, ...removed];
};

/**
 * Merges a previous and proposed `PreviewNode` tree into a single
 * unified tree where every node carries a status
 * (`unchanged`/`added`/`removed`). When `previousRoot` is null the
 * proposed tree is returned with every node marked `unchanged` so
 * the renderer behaves identically for both the first-Generate and
 * Refine flows.
 */
export const buildUnifiedDiff = ({ previousRoot, proposedRoot }) => {
  if (_.isNil(proposedRoot)) {
    return null;
  }

  if (_.isNil(previousRoot)) {
    return markStatusDeep(proposedRoot, PREVIEW_NODE_STATUS.UNCHANGED);
  }

  if (
    proposedRoot.kind === PREVIEW_NODE_KIND.GROUP &&
    previousRoot.kind === PREVIEW_NODE_KIND.GROUP &&
    proposedRoot.operator === previousRoot.operator
  ) {
    return {
      ...proposedRoot,
      status: PREVIEW_NODE_STATUS.UNCHANGED,
      children: mergeChildrenForDiff(proposedRoot.children, previousRoot.children),
    };
  }

  if (proposedRoot.kind === PREVIEW_NODE_KIND.GROUP) {
    return {
      ...proposedRoot,
      status: PREVIEW_NODE_STATUS.UNCHANGED,
      children: mergeChildrenForDiff(proposedRoot.children, [previousRoot]),
    };
  }

  if (previousRoot.kind === PREVIEW_NODE_KIND.GROUP) {
    return {
      ...previousRoot,
      status: PREVIEW_NODE_STATUS.UNCHANGED,
      children: mergeChildrenForDiff([proposedRoot], previousRoot.children),
    };
  }

  if (proposedRoot.fingerprint === previousRoot.fingerprint) {
    return markStatusDeep(proposedRoot, PREVIEW_NODE_STATUS.UNCHANGED);
  }

  return buildGroupNode(PREVIEW_GROUP_OPERATOR.AND, [
    { ...proposedRoot, status: PREVIEW_NODE_STATUS.ADDED },
    { ...previousRoot, status: PREVIEW_NODE_STATUS.REMOVED },
  ]);
};

const SORT_KEY_FALLBACK = Number.MAX_SAFE_INTEGER;

const STATUS_GROUP_ORDER = {
  [PREVIEW_NODE_STATUS.UNCHANGED]: 0,
  [PREVIEW_NODE_STATUS.ADDED]: 0,
  [PREVIEW_NODE_STATUS.REMOVED]: 1,
};

const KIND_ORDER = {
  [PREVIEW_NODE_KIND.LEAF]: 0,
  [PREVIEW_NODE_KIND.GROUP]: 1,
};

const OPERATOR_ORDER = {
  [PREVIEW_GROUP_OPERATOR.AND]: 0,
  [PREVIEW_GROUP_OPERATOR.OR]: 1,
};

const leafSortKey = node =>
  _.isEmpty(node.fieldPaths) ? '' : [...node.fieldPaths].sort().join(',');

const compareSiblings = (a, b) => {
  const statusOrderA = STATUS_GROUP_ORDER[a.status] ?? 0;
  const statusOrderB = STATUS_GROUP_ORDER[b.status] ?? 0;

  if (statusOrderA !== statusOrderB) {
    return statusOrderA - statusOrderB;
  }

  const kindOrderA = KIND_ORDER[a.kind] ?? 0;
  const kindOrderB = KIND_ORDER[b.kind] ?? 0;

  if (kindOrderA !== kindOrderB) {
    return kindOrderA - kindOrderB;
  }

  if (a.kind === PREVIEW_NODE_KIND.LEAF) {
    const keyA = leafSortKey(a);
    const keyB = leafSortKey(b);

    return keyA.localeCompare(keyB);
  }

  const operatorOrderA = OPERATOR_ORDER[a.operator] ?? SORT_KEY_FALLBACK;
  const operatorOrderB = OPERATOR_ORDER[b.operator] ?? SORT_KEY_FALLBACK;

  if (operatorOrderA !== operatorOrderB) {
    return operatorOrderA - operatorOrderB;
  }

  return countLeavesInNode(a) - countLeavesInNode(b);
};

/**
 * Returns a stably-sorted copy of the given node's children. Leaves
 * come before groups, removed siblings are pushed to the bottom (so
 * diffs read top-down), and within each bucket items are ordered
 * deterministically (leaves alphabetical by joined field paths,
 * groups by operator then descendant leaf count) so the same tree
 * always renders the same way.
 */
const sortPreviewChildren = children => [...children].sort(compareSiblings);

/**
 * Recursively sorts every group's children in the tree.
 */
export const sortPreviewTree = node => {
  if (_.isNil(node)) {
    return null;
  }

  if (node.kind === PREVIEW_NODE_KIND.LEAF) {
    return node;
  }

  const sortedChildren = sortPreviewChildren(node.children).map(child => sortPreviewTree(child));

  return { ...node, children: sortedChildren };
};

/**
 * Returns the set of node ids that should start collapsed: every
 * non-root group whose descendant leaf count exceeds the threshold.
 * Root-level groups are always rendered expanded so the user sees
 * the top-level summary without an extra click.
 */
export const resolveDefaultCollapsedNodeIds = (
  root,
  { threshold = PREVIEW_DEFAULT_COLLAPSED_LEAF_THRESHOLD } = {},
) => {
  const collapsed = new Set();

  const walk = (node, nodeId, depth) => {
    if (_.isNil(node) || node.kind !== PREVIEW_NODE_KIND.GROUP) {
      return;
    }

    if (depth > 0 && countLeavesInNode(node) > threshold) {
      collapsed.add(nodeId);
    }

    node.children.forEach((child, index) => walk(child, `${nodeId}/${index}`, depth + 1));
  };

  walk(root, 'root', 0);

  return collapsed;
};

/**
 * Recursively collects the ids of every group node in the tree so
 * the "Expand all" toggle can clear the entire collapsed set in one
 * pass without having to call setState per group.
 */
export const collectAllGroupNodeIds = root => {
  const ids = new Set();

  const walk = (node, nodeId) => {
    if (_.isNil(node) || node.kind !== PREVIEW_NODE_KIND.GROUP) {
      return;
    }

    ids.add(nodeId);
    node.children.forEach((child, index) => walk(child, `${nodeId}/${index}`));
  };

  walk(root, 'root');

  return ids;
};
